import { Link } from "react-router-dom";
import { Plus, X } from "lucide-react";
import { Skeleton } from "~/components/ui/skeleton";
import { TextOverflow } from "~/components/ui/text-overflow";
import { cn } from "~/lib/utils";

export interface PreviewItem {
  key: string;
  label: string;
  leading: React.ReactNode;
  to?: string;
  // Detaches this item, via an ✕ revealed on hover or keyboard focus.
  onRemove?: () => void;
}

interface PreviewRowProps {
  items: PreviewItem[];
  className?: string;
  // Caps how many item pills render inline before the rest collapse into a
  // "+N" tag. Omit to render every item.
  maxPreview?: number;
  // Opens the caller's picker. Renders a dashed "Add" chip after the pills.
  onAdd?: () => void;
  // Names what the Add chip attaches, for its accessible label.
  addLabel?: string;
  // Items aren't known yet. Placeholders stand in, so an unloaded row never
  // reads as an empty one.
  isLoading?: boolean;
  // A detach is in flight. Removal replaces the whole set from the list the
  // caller last saw, so a second one would resurrect the first item.
  isRemoving?: boolean;
}

const PILL = "inline-flex h-6 items-center rounded-full border border-border bg-background";

/**
 * Deliberately unequal: the avatar is a solid disc filling its box, so it has to
 * be smaller than the folder outline to carry the same weight in a 24px pill.
 */
export const PILL_AVATAR_SIZE = 16;
export const PILL_GLYPH_SIZE = 18;

// A horizontal run of pills previewing a list of items. Items with a `to`
// link out to their full page; others render as static pills.
export const PreviewRow = ({
  items,
  className,
  maxPreview,
  onAdd,
  addLabel = "item",
  isLoading,
  isRemoving,
}: PreviewRowProps) => {
  const shown = maxPreview !== undefined ? items.slice(0, maxPreview) : items;
  const overflow = items.length - shown.length;

  if (isLoading) {
    return (
      <div className={cn("flex gap-1.5", className)} aria-busy="true">
        <Skeleton className="h-6 w-32 rounded-full" />
        <Skeleton className="h-6 w-24 rounded-full" />
      </div>
    );
  }

  // An empty row with no Add chip would otherwise render as nothing.
  if (items.length === 0 && !onAdd) {
    return <p className={cn("text-xs text-muted-foreground", className)}>None</p>;
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {shown.map((item) => {
        const content = (
          <>
            {item.leading}
            <TextOverflow autoTooltip className="max-w-[9rem] text-xs text-foreground">
              {item.label}
            </TextOverflow>
          </>
        );
        const body = item.to ? (
          <Link to={item.to} className="inline-flex min-w-0 items-center gap-1.5">
            {content}
          </Link>
        ) : (
          <span className="inline-flex min-w-0 items-center gap-1.5">{content}</span>
        );

        return (
          <span
            key={item.key}
            className={cn(
              PILL,
              "group/pill pl-2",
              item.onRemove ? "pr-1" : "pr-2",
              item.to && "transition-colors hover:bg-accent"
            )}
          >
            {body}
            {item.onRemove && (
              <button
                type="button"
                aria-label={`Remove ${item.label}`}
                onClick={item.onRemove}
                disabled={isRemoving}
                className={cn(
                  "ml-1 inline-flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full",
                  "text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground",
                  "disabled:cursor-not-allowed",
                  "focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]",
                  "group-hover/pill:opacity-100"
                )}
              >
                <X className="size-3" aria-hidden="true" />
              </button>
            )}
          </span>
        );
      })}

      {overflow > 0 && <span className="text-xs text-muted-foreground">+{overflow} more</span>}

      {onAdd && (
        <button
          type="button"
          aria-label={`Add ${addLabel}`}
          onClick={onAdd}
          className={cn(
            PILL,
            "cursor-pointer gap-1 border-dashed px-2 text-xs text-muted-foreground",
            "transition-colors hover:border-solid hover:bg-accent hover:text-foreground",
            "focus-visible:outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]"
          )}
        >
          <Plus className="size-3" aria-hidden="true" />
          Add {addLabel}
        </button>
      )}
    </div>
  );
};
